/**
 * StepImport — import or paste an existing CV.
 *
 * Supported inputs:
 *  1. Paste raw text (textarea)
 *  2. Upload a PDF or DOCX file — text is extracted client-side (pdfjs / mammoth)
 *  3. Upload a .txt / .md file
 *  4. Upload a JSON backup exported from ATSmyCV (direct import, no AI call)
 *
 * For all non-JSON inputs, the extracted text is sent to /api/ai?route=parse
 * which returns structured CVData. The user then confirms a preview before
 * the data is written into useCvState via onImported(data, commonInfo).
 */

import { useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Upload, FileText } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import type { CVData } from '../../../types';
import type { CommonInfo } from '../../../hooks/useCvState';
import { COMMON_FIELDS } from '../../../hooks/useCvEditing';
import { extractCvFile } from '../../../lib/cv-file-extract';

interface StepImportProps {
  onImported: (data: CVData, commonInfo: Partial<CommonInfo>) => void;
}

type ParseState = 'idle' | 'loading' | 'done' | 'error';

export function StepImport({ onImported }: StepImportProps) {
  const [text, setText] = useState('');
  const [state, setState] = useState<ParseState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<CVData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setText('');
    setState('idle');
    setError(null);
    setParsed(null);
  };

  /* ------------------------------------------------------------------ */
  /* File upload — PDF / DOCX / TXT / JSON backup                        */
  /* ------------------------------------------------------------------ */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.currentTarget.value = '';

    setState('loading');
    setError(null);

    try {
      const extracted = await extractCvFile(file);

      // ATSmyCV JSON backup → import directly without AI parsing
      if (extracted.kind === 'json') {
        const payload = extracted.json as Record<string, unknown> | undefined;
        const cvData = (payload?.data ?? payload) as CVData | undefined;
        if (!cvData?.personalInfo || !Array.isArray(cvData.experience)) {
          throw new Error('Fichier JSON non reconnu. Utilisez un backup ATSmyCV.');
        }
        finaliseImport(cvData);
        return;
      }

      // PDF / DOCX / TXT → show extracted text + parse with AI
      setText(extracted.text);
      await parseWithAi(extracted.text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de lire le fichier.');
      setState('error');
    }
  };

  /* ------------------------------------------------------------------ */
  /* AI parse                                                            */
  /* ------------------------------------------------------------------ */
  const parseWithAi = async (content: string) => {
    setState('loading');
    setError(null);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: 'parse', text: content }),
      });
      const json = await res.json() as { data?: CVData; error?: string };
      if (!res.ok || !json.data) {
        throw new Error(json.error || 'Erreur lors de l\'analyse');
      }
      const cvData: CVData = {
        ...json.data,
        personalInfo: json.data.personalInfo ?? {},
        experience:   json.data.experience   ?? [],
        education:    json.data.education    ?? [],
        skills:       json.data.skills       ?? [],
        languages:    json.data.languages    ?? [],
        hobbies:      json.data.hobbies      ?? [],
      };
      setParsed(cvData);
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setState('error');
    }
  };

  const handleStructure = () => {
    if (!text.trim()) return;
    void parseWithAi(text);
  };

  /* ------------------------------------------------------------------ */
  /* Confirm import                                                      */
  /* ------------------------------------------------------------------ */
  const finaliseImport = (cvData: CVData) => {
    const commonInfo: Partial<CommonInfo> = {};
    for (const field of COMMON_FIELDS) {
      const val = (cvData.personalInfo as Record<string, unknown>)[field];
      if (typeof val === 'string') {
        (commonInfo as Record<string, string>)[field] = val;
      }
    }
    onImported(cvData, commonInfo);
  };

  const handleConfirm = () => {
    if (!parsed) return;
    finaliseImport(parsed);
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Importe ton CV actuel</h1>
        <p className="text-sm text-white/60">
          Charge ton CV (PDF, DOCX, TXT) ou colle son texte. L'IA extrait toutes
          les informations et pré-remplit les champs — tu vérifies ensuite.
        </p>
      </div>

      {state !== 'done' && (
        <>
          {/* File upload */}
          <div
            className="flex items-center justify-center gap-3 p-4 rounded-xl border border-dashed border-white/20 hover:border-white/40 hover:bg-white/[0.03] transition-all cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="size-5 text-white/40" />
            <div className="text-sm">
              <span className="font-semibold text-white/70">Charger un fichier</span>
              <span className="text-white/40"> — PDF, DOCX, TXT ou backup JSON ATSmyCV</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md,.text,.json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30 uppercase tracking-wider">ou coller le texte</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Paste textarea */}
          <div className="space-y-1.5">
            <Label htmlFor="wiz-import-text" className="text-sm text-white/80">
              Texte de ton CV
            </Label>
            <Textarea
              id="wiz-import-text"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setState('idle');
                setParsed(null);
                setError(null);
              }}
              placeholder="Colle ici le contenu de ton CV (copié depuis Word, PDF, LinkedIn…)"
              rows={10}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none font-mono text-sm"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <pre className="whitespace-pre-wrap font-sans">{error}</pre>
            </div>
          )}

          {/* Actions */}
          {state === 'idle' || state === 'error' ? (
            <button
              type="button"
              onClick={handleStructure}
              disabled={!text.trim()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-600/20"
            >
              <FileText className="size-4" />
              Analyser avec l'IA →
            </button>
          ) : state === 'loading' ? (
            <div className="flex items-center gap-3 text-sm text-white/60">
              <Loader2 className="size-4 animate-spin text-purple-400" />
              Analyse en cours…
            </div>
          ) : null}
        </>
      )}

      {/* Parsed preview */}
      {state === 'done' && parsed && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <CheckCircle2 className="size-4" />
            CV analysé — voici ce que l'IA a extrait :
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3 text-sm">
            {(parsed.personalInfo.firstName || parsed.personalInfo.lastName) && (
              <div>
                <span className="text-white/40 text-xs uppercase tracking-wider">Nom</span>
                <div className="text-white/90">{[parsed.personalInfo.firstName, parsed.personalInfo.lastName].filter(Boolean).join(' ')}</div>
              </div>
            )}
            {parsed.personalInfo.title && (
              <div>
                <span className="text-white/40 text-xs uppercase tracking-wider">Poste</span>
                <div className="text-white/90">{parsed.personalInfo.title}</div>
              </div>
            )}
            {parsed.experience.length > 0 && (
              <div>
                <span className="text-white/40 text-xs uppercase tracking-wider">Expériences ({parsed.experience.length})</span>
                <div className="space-y-1 mt-1">
                  {parsed.experience.slice(0, 3).map((exp, i) => (
                    <div key={i} className="text-white/80">{exp.position}{exp.company ? ` — ${exp.company}` : ''}</div>
                  ))}
                  {parsed.experience.length > 3 && <div className="text-white/40">+{parsed.experience.length - 3} de plus</div>}
                </div>
              </div>
            )}
            {parsed.skills.length > 0 && (
              <div>
                <span className="text-white/40 text-xs uppercase tracking-wider">Compétences ({parsed.skills.length})</span>
                <div className="text-white/80 mt-1">{parsed.skills.slice(0, 8).join(', ')}{parsed.skills.length > 8 ? '…' : ''}</div>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleConfirm}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/30"
            >
              <CheckCircle2 className="size-4" />
              Utiliser ces données →
            </button>
            <button
              type="button"
              onClick={reset}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white/50 hover:text-white/80 transition-colors"
            >
              Recommencer
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-white/30">
        Tes données restent sur ce serveur et ne sont jamais partagées.
      </p>
    </div>
  );
}
